import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Send, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeader from "../components/SectionHeader";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useTranslation();
  const { siteContent, services } = useSiteData();
  const { pick } = useLocaleText();
  const location = useLocation();
  const prefilledService = location.state?.service || "";

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service: prefilledService || "General Inquiry",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (prefilledService) setForm((f) => ({ ...f, service: prefilledService }));
  }, [prefilledService]);

  const phones = siteContent?.phones || ["+974 50871458", "+974 77772905"];
  const email = siteContent?.email || "info@ragimetrdingcontracting.com";
  const crNumber = siteContent?.cr_number || "220664";
  const locationText = pick(siteContent?.location) || "Doha, Qatar";

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) {
      toast.error("Please add a slightly longer message (min 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.successMsg"));
      setForm({ name: "", email: "", phone: "", service: "General Inquiry", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || t("contact.errorMsg");
      toast.error(typeof detail === "string" ? detail : t("contact.errorMsg"));
    } finally {
      setSubmitting(false);
    }
  };

  const rental = (services || []).filter((s) => s.type === "rental");
  const other = (services || []).filter((s) => s.type === "other");
  const serviceOptions = [
    "General Inquiry",
    ...rental.map((e) => `Rental — ${pick(e.name)}`),
    ...other.map((s) => pick(s.name)),
  ];

  return (
    <div data-testid="page-contact">
      <SEO title={t("nav.contact")} description={t("contact.intro")} path="/contact" />

      <section className="relative bg-[#111111] text-white py-8 md:py-12 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-contact.jpg" alt="RTC contact" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/55 to-transparent rtl:bg-gradient-to-l" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-[2px] bg-[#FF5A00]" />
            <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">{t("contact.eyebrow")}</span>
          </div>
          <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {t("contact.title1")} <br /><span className="text-[#FF5A00]">{t("contact.title2")}</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/80">{t("contact.intro")}</p>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* ── Form (left) ── */}
          <div className="lg:col-span-7">
            <SectionHeader eyebrow={t("contact.formEyebrow")} title={t("contact.formTitle")} subtitle={t("contact.formSubtitle")} />

            <form onSubmit={handleSubmit} className="mt-8 bg-white border-2 border-[#111111] block-shadow-orange p-5 sm:p-7 md:p-8 space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                <div className="group">
                  <label className="block font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-2 group-focus-within:text-[#FF5A00] transition-colors">{t("contact.fullName")} <span className="text-[#FF5A00]">*</span></label>
                  <div className="relative">
                    <input required name="name" value={form.name} onChange={handleChange}
                      className="w-full bg-white border-b-2 border-[#111111] px-0 py-2.5 outline-none focus:border-[#FF5A00] transition-colors text-base placeholder:text-[#9CA3AF]"
                      placeholder="Jane Doe"
                      data-testid="contact-input-name" />
                  </div>
                </div>
                <div className="group">
                  <label className="block font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-2 group-focus-within:text-[#FF5A00] transition-colors">{t("contact.email")} <span className="text-[#FF5A00]">*</span></label>
                  <input required type="email" name="email" value={form.email} onChange={handleChange}
                    className="w-full bg-white border-b-2 border-[#111111] px-0 py-2.5 outline-none focus:border-[#FF5A00] transition-colors text-base placeholder:text-[#9CA3AF]"
                    placeholder="you@company.com"
                    data-testid="contact-input-email" />
                </div>
                <div className="group">
                  <label className="block font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-2 group-focus-within:text-[#FF5A00] transition-colors">{t("contact.phone")}</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="+974 5000 0000"
                    className="w-full bg-white border-b-2 border-[#111111] px-0 py-2.5 outline-none focus:border-[#FF5A00] transition-colors text-base placeholder:text-[#9CA3AF]"
                    data-testid="contact-input-phone" />
                </div>
                <div className="group">
                  <label className="block font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-2 group-focus-within:text-[#FF5A00] transition-colors">{t("contact.serviceNeeded")}</label>
                  <select name="service" value={form.service} onChange={handleChange}
                    className="w-full bg-white border-b-2 border-[#111111] px-0 py-2.5 outline-none focus:border-[#FF5A00] transition-colors text-base appearance-none cursor-pointer"
                    style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23FF5A00' width='16' height='16'><path d='M7 10l5 5 5-5z'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right center" }}
                    data-testid="contact-input-service">
                    {serviceOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>

              <div className="group">
                <label className="block font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-[#52525B] mb-2 group-focus-within:text-[#FF5A00] transition-colors">{t("contact.projectDetails")} <span className="text-[#FF5A00]">*</span></label>
                <textarea required name="message" rows="5" value={form.message} onChange={handleChange} placeholder={t("contact.placeholderProject")}
                  className="w-full bg-[#FAFAFA] border-2 border-[#E5E7EB] focus:border-[#FF5A00] focus:bg-white px-4 py-3 outline-none transition-all resize-none text-base placeholder:text-[#9CA3AF] rounded-sm"
                  data-testid="contact-input-message" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pt-2 border-t border-[#E5E7EB]">
                <p className="text-[10px] text-[#52525B] font-mono-rtc uppercase tracking-widest leading-relaxed max-w-xs">{t("contact.consent")}</p>
                <button type="submit" disabled={submitting}
                  className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#111111] transition-colors disabled:opacity-60 shrink-0 w-full sm:w-auto justify-center shadow-[3px_3px_0_#111111] hover:shadow-[5px_5px_0_#FF5A00]"
                  data-testid="contact-submit">
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> {t("common.sending")}</> : <><Send size={16} /> {t("common.sendMessage")}</>}
                </button>
              </div>
            </form>
          </div>

          {/* ── Reach Us + Map (right) ── */}
          <aside className="lg:col-span-5 flex flex-col gap-6">
            {/* Compact REACH US box */}
            <div className="bg-[#111111] text-white p-6 md:p-7 block-shadow-orange">
              <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">{t("contact.infoEyebrow")}</span>
              <h3 className="font-display uppercase font-bold text-2xl sm:text-3xl mt-1">{t("contact.infoTitle")}</h3>

              {/* Address */}
              <div className="mt-5 flex items-start gap-3">
                <span className="bg-[#FF5A00] text-[#111111] p-2 shrink-0"><MapPin size={16} /></span>
                <div className="min-w-0">
                  <div className="font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-white/60">{t("contact.address")}</div>
                  <div className="font-bold mt-0.5 text-sm">{locationText}</div>
                  <div className="text-white/60 text-[11px] font-mono-rtc uppercase tracking-wider">{t("common.crNumber")} {crNumber}</div>
                </div>
              </div>

              {/* Email */}
              <div className="mt-4 flex items-start gap-3">
                <span className="bg-[#FF5A00] text-[#111111] p-2 shrink-0"><Mail size={16} /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-white/60">{t("contact.emailLabel")}</div>
                  <a href={`mailto:${email}`} className="font-bold mt-0.5 hover:text-[#FF5A00] transition-colors break-all block text-sm" data-testid="contact-email-link" dir="ltr">{email}</a>
                </div>
              </div>

              {/* Phones — 2 columns on the right side */}
              <div className="mt-4">
                <div className="font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2 ltr:pl-9 rtl:pr-9">{t("contact.phoneLabel")}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 ltr:pl-9 rtl:pr-9">
                  {phones.map((p, idx) => (
                    <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-2 font-bold text-sm text-white hover:text-[#FF5A00] transition-colors" data-testid={`contact-phone-${idx}`} dir="ltr">
                      <Phone size={12} className="text-[#FF5A00] shrink-0" />
                      <span>{p}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Map — taller now to balance with form */}
            <div className="border-2 border-[#111111] block-shadow overflow-hidden flex-1 min-h-[280px]">
              <iframe title="RTC Doha location" src="https://www.google.com/maps?q=Doha,Qatar&output=embed" width="100%" height="100%" style={{ border: 0, minHeight: 280, display: 'block' }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />
            </div>
          </aside>
        </div>
      </section>

      <section className="py-8 md:py-10 bg-[#FF5A00] text-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase font-bold">{t("common.urgentCall")}</h3>
          <a href={`tel:${phones[0].replace(/\s/g, "")}`} className="mt-6 inline-flex items-center gap-2 bg-[#111111] text-white px-8 py-4 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors" data-testid="contact-urgent-call">
            <Phone size={18} /> <span dir="ltr">{phones[0]}</span> <ArrowRight size={16} className="rtl:rotate-180" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
